import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Dialog, Box, Typography, TextField, IconButton, List, ListItem,
  ListItemButton, Avatar, CircularProgress, InputAdornment, Chip, Stack, Button,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Close, Search, Storefront, Warehouse, ArrowBack, ExpandMore, ExpandLess } from '@mui/icons-material';
import { useLazyGetProductStockSummaryQuery } from '../../services/inventoryApi';
import { useLazyGetProductsQuery } from '../../services/productApi';

// Beyond this many in-stock locations, the list gets a filter box and
// collapses to a preview — a popular SKU can realistically sit in stock
// at dozens of stores, and dumping all of them as padded cards at once
// stops being scannable.
const STORE_PREVIEW_COUNT = 8;
const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_PAGE_SIZE = 20;

// A tied sort rank has no guaranteed stable order across separate paginated
// requests, so the same row can occasionally come back on two consecutive
// pages — dedupe on append so neither a duplicate React key nor a repeated
// row reaches the cashier.
const appendUnique = (prev, incoming) => {
  const seenIds = new Set(prev.map((p) => p.id));
  return prev.concat(incoming.filter((p) => !seenIds.has(p.id)));
};

const uniqueById = (list) => appendUnique([], list);

// Warehouses hold the bulk of replenishment stock, so a cashier asking
// "where else is this?" needs them alongside stores.
const LOOKUP_LOCATION_TYPES = ['Store', 'Warehouse'];

// Store and Warehouse ids are separate sequences — match on type too, or a
// warehouse that happens to share the current store's id gets tagged as it.
const isCurrentStore = (location, currentStoreId) =>
  location.type === 'Store' && location.id === currentStoreId;

const StockLookupModal = ({ open, onClose, initialProduct = null }) => {
  const currentStoreId = useSelector((state) => state.auth.store?.id);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [storeFilter, setStoreFilter] = useState('');
  const [showAllStores, setShowAllStores] = useState(false);

  const [triggerSummary, { data, isFetching, isError }] = useLazyGetProductStockSummaryQuery();

  // Opened from the product info card — jump straight to that product's
  // breakdown instead of making the cashier search for it again.
  useEffect(() => {
    if (!open) return;
    if (initialProduct) {
      setSelectedProduct(initialProduct);
      setSearchTerm(initialProduct.name);
      setStoreFilter('');
      setShowAllStores(false);
      triggerSummary(initialProduct.id);
    } else {
      setSelectedProduct(null);
      setSearchTerm('');
    }
  }, [open, initialProduct, triggerSummary]);

  const [triggerProductSearch, { isFetching: isSearchingProducts }] = useLazyGetProductsQuery();
  const [serverResults, setServerResults] = useState(null);
  const [searchPage, setSearchPage] = useState(1);
  const [searchHasMore, setSearchHasMore] = useState(false);
  const [isFetchingMoreResults, setIsFetchingMoreResults] = useState(false);
  const searchDebounceRef = useRef(null);
  const searchTermRef = useRef('');

  useEffect(() => {
    setServerResults(null); // clear the previous term's results immediately, not just on debounce
    setSearchPage(1);
    setSearchHasMore(false);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);

    const term = searchTerm.trim();
    searchTermRef.current = term;
    if (selectedProduct || term.length < 2) return undefined;

    searchDebounceRef.current = setTimeout(() => {
      triggerProductSearch({ search: term, page: 1, per_page: SEARCH_PAGE_SIZE }).unwrap()
        .then((res) => {
          setServerResults(uniqueById(res?.data || []));
          setSearchHasMore((res?.pagination?.total_pages || 1) > 1);
        })
        .catch(() => {});
    }, SEARCH_DEBOUNCE_MS);

    return () => { if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current); };
  }, [searchTerm, selectedProduct, triggerProductSearch]);

  // Same reasoning as the billing search bar: the backend doesn't sort
  // matches by relevance/stock, so a large SKU family can push the right
  // variant past the first page. Load more on scroll instead of ever
  // fetching hundreds of rows up front.
  const loadMoreResults = () => {
    const term = searchTermRef.current;
    if (term.length < 2 || !searchHasMore || isFetchingMoreResults) return;
    const nextPage = searchPage + 1;
    setIsFetchingMoreResults(true);
    triggerProductSearch({ search: term, page: nextPage, per_page: SEARCH_PAGE_SIZE }).unwrap()
      .then((res) => {
        setServerResults((prev) => appendUnique(prev || [], res?.data || []));
        setSearchPage(nextPage);
        setSearchHasMore(nextPage < (res?.pagination?.total_pages || 1));
      })
      .catch(() => {})
      .finally(() => setIsFetchingMoreResults(false));
  };

  const results = serverResults || [];

  const storeRows = useMemo(() => {
    const summary = data?.data?.summary || [];
    return summary
      .filter((row) => LOOKUP_LOCATION_TYPES.includes(row.location?.type))
      .sort((a, b) => {
        if (isCurrentStore(a.location, currentStoreId)) return -1;
        if (isCurrentStore(b.location, currentStoreId)) return 1;
        return b.total_quantity - a.total_quantity;
      });
  }, [data, currentStoreId]);

  const filteredStoreRows = useMemo(() => {
    const term = storeFilter.trim().toLowerCase();
    if (!term) return storeRows;
    return storeRows.filter((row) => row.location.name?.toLowerCase().includes(term));
  }, [storeRows, storeFilter]);

  const visibleStoreRows = showAllStores || storeFilter.trim()
    ? filteredStoreRows
    : filteredStoreRows.slice(0, STORE_PREVIEW_COUNT);

  const totalUnits = useMemo(
    () => storeRows.reduce((sum, row) => sum + row.total_quantity, 0),
    [storeRows]
  );

  const handleClose = () => {
    setSearchTerm('');
    setSelectedProduct(null);
    setStoreFilter('');
    setShowAllStores(false);
    onClose();
  };

  const handleSelect = (product) => {
    setSelectedProduct(product);
    setSearchTerm(product.name);
    setStoreFilter('');
    setShowAllStores(false);
    triggerSummary(product.id);
  };

  const handleBackToSearch = () => {
    setSelectedProduct(null);
    setSearchTerm('');
    setStoreFilter('');
    setShowAllStores(false);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, sm: '12px' },
          m: { xs: 0, sm: 3 },
          display: 'flex',
          flexDirection: 'column',
          maxHeight: { xs: '100%', sm: '80vh' },
          height: { xs: '100%', sm: 'auto' },
          width: '100%',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2.5, py: 2, borderBottom: '1px solid #F1F5F9', flexShrink: 0 }}>
        <Box sx={{ width: 36, height: 36, borderRadius: '10px', bgcolor: '#EEF2FF', color: '#4F46E5', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Storefront sx={{ fontSize: 19 }} />
        </Box>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography fontWeight={800} fontSize="0.95rem" color="#1E293B">Stock at Other Locations</Typography>
          <Typography fontSize="0.7rem" color="#94A3B8">Read-only lookup — nothing is reserved or moved</Typography>
        </Box>
        <IconButton size="small" onClick={handleClose} sx={{ color: '#64748B' }}>
          <Close fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{
        p: { xs: 2, sm: 2.5 }, overflowY: 'auto', flexGrow: 1, minHeight: 0,
        '&::-webkit-scrollbar': { width: '4px' },
        '&::-webkit-scrollbar-thumb': { bgcolor: '#CBD5E1', borderRadius: '10px' },
        '&::-webkit-scrollbar-button': { display: 'none', width: 0, height: 0 },
      }}>
        <TextField
          fullWidth
          autoFocus
          size="small"
          placeholder="Search product by name or SKU..."
          value={searchTerm}
          disabled={!!selectedProduct}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                {selectedProduct
                  ? <IconButton size="small" onClick={handleBackToSearch} sx={{ ml: -1, color: '#4F46E5' }}><ArrowBack sx={{ fontSize: 17 }} /></IconButton>
                  : <Search sx={{ color: '#94A3B8', fontSize: 18 }} />}
              </InputAdornment>
            ),
            endAdornment: !selectedProduct && isSearchingProducts
              ? <InputAdornment position="end"><CircularProgress size={14} sx={{ color: '#94A3B8' }} /></InputAdornment>
              : null,
          }}
          sx={{
            '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: '#F8FAFC', fontSize: '0.9rem' },
            '& .Mui-disabled': { WebkitTextFillColor: '#1E293B !important', fontWeight: 700 },
          }}
        />

        {/* Search results */}
        {results.length > 0 && (
          <List
            disablePadding
            onScroll={(e) => {
              if (!searchHasMore || isFetchingMoreResults) return;
              const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
              if (scrollHeight - scrollTop - clientHeight < 80) loadMoreResults();
            }}
            sx={{
              border: '1px solid #E2E8F0', borderRadius: '10px', mt: 1.5,
              maxHeight: 280, overflowY: 'auto',
              '&::-webkit-scrollbar': { width: '4px' },
              '&::-webkit-scrollbar-thumb': { bgcolor: '#CBD5E1', borderRadius: '10px' },
              '&::-webkit-scrollbar-button': { display: 'none', width: 0, height: 0 },
            }}>
            {results.map((p) => (
              <ListItem key={p.id} disablePadding divider>
                <ListItemButton onClick={() => handleSelect(p)} sx={{ py: 1.1, px: 1.5, gap: 1.25, '&:hover': { bgcolor: '#F8FAFC' } }}>
                  <Avatar sx={{ bgcolor: '#EEF2FF', color: '#4F46E5', fontWeight: 800, fontSize: '0.78rem', width: 34, height: 34 }}>
                    {p.name?.[0]?.toUpperCase()}
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography noWrap fontWeight={700} fontSize="0.82rem" color="#1E293B">{p.name}</Typography>
                    <Typography fontSize="0.7rem" color="#94A3B8">{p.sku}</Typography>
                  </Box>
                </ListItemButton>
              </ListItem>
            ))}
            {isFetchingMoreResults && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                <CircularProgress size={16} sx={{ color: '#94A3B8' }} />
              </Box>
            )}
          </List>
        )}

        {!selectedProduct && searchTerm.trim().length >= 2 && results.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4, color: '#94A3B8' }}>
            <Search sx={{ fontSize: 34, mb: 1, opacity: 0.4 }} />
            <Typography fontSize="0.82rem" fontWeight={600} color="#64748B">No products found</Typography>
          </Box>
        )}

        {!selectedProduct && searchTerm.trim().length < 2 && (
          <Box sx={{ textAlign: 'center', py: 5, color: '#CBD5E1' }}>
            <Storefront sx={{ fontSize: 34, mb: 1 }} />
            <Typography fontSize="0.8rem" color="#94A3B8">Search a product to see its stock at every store and warehouse</Typography>
          </Box>
        )}

        {/* Store breakdown for the selected product */}
        {selectedProduct && (
          <Box sx={{ mt: 2 }}>
            {isFetching ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                <CircularProgress size={22} />
              </Box>
            ) : isError ? (
              <Box sx={{ textAlign: 'center', py: 4, color: '#94A3B8' }}>
                <Typography fontSize="0.82rem" fontWeight={600} color="#DC2626">Couldn't load stock right now</Typography>
                <Typography fontSize="0.72rem" color="#94A3B8">Try again in a moment</Typography>
              </Box>
            ) : storeRows.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4, color: '#94A3B8' }}>
                <Typography fontSize="0.82rem" fontWeight={600} color="#64748B">No stock found at any store or warehouse</Typography>
              </Box>
            ) : (
              <>
                {/* Summary strip — the headline number a cashier actually needs before scanning rows */}
                <Box sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1,
                  bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '9px', px: 1.5, py: 1, mb: 1.25,
                }}>
                  <Typography fontSize="0.76rem" fontWeight={700} color="#334155">
                    In stock at {storeRows.length} location{storeRows.length !== 1 ? 's' : ''}
                  </Typography>
                  <Typography fontSize="0.76rem" fontWeight={800} color="#1E293B" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {totalUnits} units total
                  </Typography>
                </Box>

                {/* Filter — only worth the space once the list is long enough to need narrowing */}
                {storeRows.length > STORE_PREVIEW_COUNT && (
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Filter locations by name..."
                    value={storeFilter}
                    onChange={(e) => setStoreFilter(e.target.value)}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><Search sx={{ color: '#94A3B8', fontSize: 16 }} /></InputAdornment>,
                    }}
                    sx={{ mb: 1.25, '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: '#F8FAFC', fontSize: '0.82rem' } }}
                  />
                )}

                {filteredStoreRows.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 3, color: '#94A3B8' }}>
                    <Typography fontSize="0.8rem" fontWeight={600} color="#64748B">No location matches "{storeFilter}"</Typography>
                  </Box>
                ) : (
                  <List disablePadding sx={{
                    border: '1px solid #E2E8F0', borderRadius: '10px',
                    maxHeight: 320, overflowY: 'auto',
                    '&::-webkit-scrollbar': { width: '4px' },
                    '&::-webkit-scrollbar-thumb': { bgcolor: '#CBD5E1', borderRadius: '10px' },
                    '&::-webkit-scrollbar-button': { display: 'none', width: 0, height: 0 },
                  }}>
                    {visibleStoreRows.map((row, idx) => {
                      const mine = isCurrentStore(row.location, currentStoreId);
                      const isWarehouse = row.location.type === 'Warehouse';
                      const LocationIcon = isWarehouse ? Warehouse : Storefront;
                      const qty = row.total_quantity;
                      const qtyColor = qty <= 5 ? '#92400E' : '#16A34A';
                      return (
                        <Box
                          key={`${row.location.type}-${row.location.id}`}
                          sx={{
                            display: 'flex', alignItems: 'center', gap: 1, px: 1.25, py: 0.9,
                            borderTop: idx > 0 ? '1px solid #F1F5F9' : 'none',
                            ...(mine && { bgcolor: '#FAFAFE' }),
                          }}
                        >
                          <LocationIcon sx={{ fontSize: 15, color: '#94A3B8', flexShrink: 0 }} />
                          <Typography noWrap fontWeight={mine ? 800 : 600} fontSize="0.8rem" color="#1E293B" sx={{ flexGrow: 1, minWidth: 0 }}>
                            {row.location.name}
                          </Typography>
                          {isWarehouse && (
                            <Chip label="Warehouse" size="small" sx={{ height: 16, fontSize: '0.58rem', fontWeight: 700, bgcolor: '#F1F5F9', color: '#475569', flexShrink: 0 }} />
                          )}
                          {mine && (
                            <Chip label="Your store" size="small" sx={{ height: 16, fontSize: '0.58rem', fontWeight: 700, bgcolor: '#EEF2FF', color: '#4F46E5', flexShrink: 0 }} />
                          )}
                          <Typography fontWeight={800} fontSize="0.86rem" color={qtyColor} sx={{ fontVariantNumeric: 'tabular-nums', flexShrink: 0, minWidth: 26, textAlign: 'right' }}>
                            {qty}
                          </Typography>
                        </Box>
                      );
                    })}
                  </List>
                )}

                {!storeFilter.trim() && filteredStoreRows.length > STORE_PREVIEW_COUNT && (
                  <Button
                    fullWidth
                    size="small"
                    onClick={() => setShowAllStores((v) => !v)}
                    endIcon={showAllStores ? <ExpandLess sx={{ fontSize: '16px !important' }} /> : <ExpandMore sx={{ fontSize: '16px !important' }} />}
                    sx={{ mt: 1, textTransform: 'none', fontWeight: 700, fontSize: '0.76rem', color: '#4F46E5' }}
                  >
                    {showAllStores ? 'Show fewer locations' : `Show all ${filteredStoreRows.length} locations`}
                  </Button>
                )}
              </>
            )}
          </Box>
        )}
      </Box>
    </Dialog>
  );
};

export default StockLookupModal;
